import React, { useMemo, useState } from 'react'
import { AutoComplete, Form, Input, Modal, notification, Radio } from 'antd'
import isNil from 'lodash/isNil'
import { useMyClientsQuery } from 'enrollment-platform/api/persons'
import { useCreateBillingProfileMutation } from '../../api/client-billing-profile-api-client'
import { extractError } from '../../api/extract-error'
import { isValidEmail } from '../helpers'
import { useFundingInviteAccess } from '../UserManagement/useFundingInviteAccess'
import {
  FormHint,
  FormLabel,
  RadioOptionRow,
} from './ClientBillingProfileManagement.styles'
import { AccessGrantResultsModal } from './AccessGrantResultsModal'
import { useAccessGrantRun } from './useAccessGrantRun'
import type { PaymentType } from './types'

const BILLING_PAYMENT_TYPE_OPTIONS = [
  { label: 'Wire', value: 'WIRE' },
  { label: 'Stripe', value: 'STRIPE' },
]

interface NewCompanyFormValues {
  clientName: string
  emails?: string
  paymentType: PaymentType
}

interface NewCompanyModalProps {
  open: boolean
  onClose: (createdId?: number) => void
}

/** Splits a pasted or typed list on commas, semicolons and newlines. */
const parseEmails = (value: string): string[] =>
  value
    .split(/[\s,;]+/)
    .map((email) => email.trim())
    .filter((email) => email.length > 0)

/**
 * Creates a company's billing profile and grants the addresses entered Finance
 * Manager access. Separate from the profile modal because the two differ in
 * more than their submit: here the company is picked and the accounts are typed
 * as a list, and the grant is what makes them managers rather than contacts.
 */
const NewCompanyModal = ({ open, onClose }: NewCompanyModalProps) => {
  const [form] = Form.useForm<NewCompanyFormValues>()
  const [clientSearch, setClientSearch] = useState('')
  const { data: clients = [], isLoading: clientsLoading } = useMyClientsQuery()
  const inviteAccess = useFundingInviteAccess()

  const options = useMemo(() => {
    const term = clientSearch.trim().toLowerCase()
    const matches =
      term.length === 0
        ? clients
        : clients.filter((client) => client.name.toLowerCase().includes(term))
    return matches.slice(0, 50).map((client) => ({ value: client.name }))
  }, [clients, clientSearch])

  // The grant is reported on its own: the profile is already written by then,
  // so a failure here must not read as the company having failed to save.
  const accessGrants = useAccessGrantRun(
    'The company was created, but granting access failed'
  )

  const startGrants = (
    clientPublicKey: string | null,
    clientName: string,
    emails: string[]
  ) => {
    if (!inviteAccess.allowed || isNil(clientPublicKey)) return
    accessGrants.grantAccess(
      `Billing profile created for ${clientName}.`,
      emails.map((email) => ({
        email,
        personId: null,
        clientPublicKeys: [clientPublicKey],
        roleIds: inviteAccess.roleIds,
      }))
    )
  }

  const createMutation = useCreateBillingProfileMutation({
    onSuccess: (created, submitted) => {
      notification.success({ message: 'Company created successfully' })
      startGrants(
        created.clientPublicKey,
        submitted.clientName,
        submitted.toEmails
      )
      form.resetFields()
      setClientSearch('')
      onClose(created.id)
    },
    onError: (error) => {
      notification.error({
        message: extractError(error, 'Failed to create the company'),
      })
    },
  })

  const handleSubmit = async () => {
    let values: NewCompanyFormValues
    try {
      values = await form.validateFields()
    } catch {
      return
    }

    const client = clients.find((option) => option.name === values.clientName)
    if (isNil(client)) {
      form.setFields([
        { name: 'clientName', errors: ['Pick a company from the list'] },
      ])
      return
    }

    createMutation.mutate({
      clientName: client.name,
      clientPublicKey: client.id,
      toEmails: parseEmails(values.emails ?? ''),
      paymentType: values.paymentType,
    })
  }

  const handleCancel = () => {
    form.resetFields()
    setClientSearch('')
    onClose()
  }

  return (
    <>
      <Modal
        open={open}
        title="New company"
        onCancel={handleCancel}
        onOk={handleSubmit}
        okText="Create company"
        confirmLoading={createMutation.isPending}
        width={540}
        style={{ maxWidth: 'calc(100vw - 32px)' }}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
          initialValues={{ paymentType: 'WIRE' }}
        >
          <Form.Item
            name="clientName"
            label={<FormLabel>Company name</FormLabel>}
            rules={[{ required: true, message: 'Please enter a company' }]}
          >
            <AutoComplete
              options={options}
              onSearch={setClientSearch}
              disabled={clientsLoading}
              placeholder={
                clientsLoading
                  ? 'Loading companies…'
                  : 'e.g., Acme Solutions Inc.'
              }
            />
          </Form.Item>

          <Form.Item
            name="emails"
            label={<FormLabel>Invoice recipients</FormLabel>}
            extra={
              <FormHint>
                {inviteAccess.allowed
                  ? 'Recipients you add here are granted Finance Manager access for this company. Anyone without an account is emailed an invitation.'
                  : "Recipients you add here receive this company's invoices."}
              </FormHint>
            }
            rules={[
              {
                validator: (_, value?: string) => {
                  const emails = parseEmails(value ?? '')
                  if (emails.length === 0) {
                    return Promise.reject(
                      new Error('At least one email is required')
                    )
                  }
                  const invalid = emails.filter((email) => !isValidEmail(email))
                  return invalid.length === 0
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error(
                          `Invalid email address${invalid.length > 1 ? 'es' : ''}: ${invalid.join(', ')}`
                        )
                      )
                },
              },
            ]}
          >
            <Input.TextArea
              rows={3}
              placeholder="billing@company.com, ap@company.com"
            />
          </Form.Item>

          <Form.Item
            name="paymentType"
            label={<FormLabel>Payment type</FormLabel>}
            rules={[
              { required: true, message: 'Please select a payment type' },
            ]}
          >
            <Radio.Group>
              <RadioOptionRow>
                {BILLING_PAYMENT_TYPE_OPTIONS.map((option) => (
                  <Radio key={option.value} value={option.value}>
                    {option.label}
                  </Radio>
                ))}
              </RadioOptionRow>
            </Radio.Group>
          </Form.Item>
        </Form>
      </Modal>

      {/* Outlives the form: this modal closes itself as soon as the company is
          written, while the grant is still running. */}
      <AccessGrantResultsModal state={accessGrants} />
    </>
  )
}

export { NewCompanyModal }
